import React from 'react';
import CableSVGLine from './CableSVGLine';
import { renderTerminalSchematic } from '../../utils/terminalRenderLogic';
import { formatSezione, preprocessTextForWrapping, estimateTextWidth, getEffectiveSectionForDisplay, getSiglaturaPerStampa, extractSiglaturaFromCollegamento } from '../../utils/cableLogic';
import { SECTION_SCALE_MAP, BASE_TERMINAL_SCALE, MIN_TERMINAL_SCALE, MAX_TERMINAL_SCALE, TERMINAL_SCALE_PROPORTIONAL_FACTOR } from '../../constants/terminalConstants';

// Componente per visualizzazione cavo singolo
const SingleCableVisualization = ({ cable, hoveredCable, setHoveredCable, isMobile, settings }) => {
  const svgWidth = isMobile ? 400 : 600;
  const svgHeight = 120;
  // Posizioni per le scritte di collegamento e nomi terminali
  const x1 = isMobile ? 80 : 80;
  const x2 = isMobile ? 320 : 520;
  const y1 = 60, y2 = 60;
  // ✅ Posizioni separate per i terminali, spostati verso l'esterno
  const terminalAX = isMobile ? 70 : 70;  // Terminale A più a sinistra
  const terminalBX = isMobile ? 330 : 530; // Terminale B più a destra
  const xNum = isMobile ? 200 : 300;
  const yNum = 30;
  const isHovered = hoveredCable === cable.originalIndex;
  const tooltipText = `${cable.colore} • ${formatSezione(cable.sezione, settings.useAWG)} • ${cable.lunghezza}mm`;
  const tooltipFontSize = 14;
  const tooltipPadding = 24;
  const tooltipWidth = estimateTextWidth(tooltipText, tooltipFontSize) + tooltipPadding;
  const tooltipX = xNum;

  // Calcola la scala dei terminali basata sulla sezione del cavo
  const calculateTerminalScale = (sezione, useAWG) => {
    // Converti AWG in mm² se necessario
    const effectiveSection = getEffectiveSectionForDisplay(sezione, useAWG);

    // Se la sezione è esattamente nella mappa, usa quel valore
    if (SECTION_SCALE_MAP[effectiveSection]) {
      return SECTION_SCALE_MAP[effectiveSection];
    }

    // Altrimenti calcola una scala proporzionale
    // Formula: baseScale + (sezione / 100) * FACTOR
    const proportionalScale = BASE_TERMINAL_SCALE + (effectiveSection / 100) * TERMINAL_SCALE_PROPORTIONAL_FACTOR;
    
    // Limita la scala tra MIN e MAX
    return Math.max(MIN_TERMINAL_SCALE, Math.min(MAX_TERMINAL_SCALE, proportionalScale));
  };

  const terminalScale = calculateTerminalScale(cable.sezione, settings.useAWG);

  // ✅ Calcola l'offset orizzontale delle siglature usando la stessa scala dei terminali
  // Converte la scala del terminale in un offset in pixel/unità SVG
  // La scala va da ~0.04 a ~0.50, quindi moltiplichiamo per un fattore per ottenere pixel
  const siglaturaOffset = terminalScale * 500; // Fattore di conversione scala -> pixel

  // ✅ Funzione per verificare se è un cavo di terra
  const isEarthWire = (colore) => {
    return colore === 'GI/VE' || colore === 'YE/GN';
  };

  // ✅ Funzione per verificare se una siglatura è valida (non placeholder)
  const isValidSiglatura = (sig) => {
    if (!sig || String(sig).trim() === '') return false;
    const sigTrimmed = String(sig).trim();
    // Escludi placeholder e siglature di terra
    const invalidSigs = ['[Senza siglatura]', '[No marking]', 'TERRA', 'GND', 'EARTH'];
    return !invalidSigs.includes(sigTrimmed);
  };

  // ✅ Ottieni le siglature separate per A e B (come nei multipolari)
  let siglaturaA = '';
  let siglaturaB = '';
  
  // Non renderizzare siglature per cavi di terra
  const isCableEarthWire = isEarthWire(cable.colore);
  
  if (!isCableEarthWire) {
    if (settings.marcaturePerCollegamento) {
      // Modalità collegamento: estrai dalle siglature dei collegamenti
      siglaturaA = extractSiglaturaFromCollegamento(cable.collegamentoA) || '';
      siglaturaB = extractSiglaturaFromCollegamento(cable.collegamentoB) || '';
    } else {
      // Modalità normale: usa la siglatura generale per entrambi i lati
      siglaturaA = cable.siglatura || '';
      siglaturaB = cable.siglatura || '';
    }
  }
  
  // ✅ Verifica che le siglature siano valide (non placeholder)
  const hasSiglaturaA = isValidSiglatura(siglaturaA);
  const hasSiglaturaB = isValidSiglatura(siglaturaB);

  // Posizioni delle siglature vicino ai terminali (come nei multipolari)
  // Siglatura A: dopo il terminale A, verso destra
  // ✅ Posizione X dinamica: usa la stessa scala dei terminali per spostarsi verso il centro
  const siglaturaAX = x1 + siglaturaOffset;
  const siglaturaAY = y1;
  const siglaturaATextWidth = hasSiglaturaA ? estimateTextWidth(siglaturaA, 12) : 0;
  
  // Siglatura B: prima del terminale B, verso sinistra
  // ✅ Posizione X dinamica: usa la stessa scala dei terminali per spostarsi verso il centro
  const siglaturaBX = x2 - siglaturaOffset;
  const siglaturaBY = y2;
  const siglaturaBTextWidth = hasSiglaturaB ? estimateTextWidth(siglaturaB, 12) : 0;

  return (
    <svg
      className="w-full"
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      onMouseEnter={() => setHoveredCable(cable.originalIndex)}
      onMouseLeave={() => setHoveredCable(null)}
      style={{ cursor: 'pointer' }}
    >
      {/* Linea del cavo */}
      <CableSVGLine
        cable={cable}
        x1={x1} y1={y1} x2={x2} y2={y2}
        isHovered={isHovered}
        useAWG={settings.useAWG}
      />

      {/* Etichetta siglatura A (vicino al terminale A) */}
      {hasSiglaturaA && (
        <g>
          <rect
            x={siglaturaAX - 6}
            y={siglaturaAY - 8}
            width={siglaturaATextWidth + 12}
            height={16}
            rx={3}
            fill="white"
            stroke="#D1D5DB"
            strokeWidth={1}
            filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
          />
          <text
            x={siglaturaAX}
            y={siglaturaAY + 1}
            className="text-xs font-bold fill-gray-800"
            textAnchor="start"
            dominantBaseline="middle"
          >
            {siglaturaA}
          </text>
        </g>
      )}

      {/* Etichetta siglatura B (vicino al terminale B) */}
      {hasSiglaturaB && (
        <g>
          <rect
            x={siglaturaBX - siglaturaBTextWidth - 6}
            y={siglaturaBY - 8}
            width={siglaturaBTextWidth + 12}
            height={16}
            rx={3}
            fill="white"
            stroke="#D1D5DB"
            strokeWidth={1}
            filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
          />
          <text
            x={siglaturaBX}
            y={siglaturaBY + 1}
            className="text-xs font-bold fill-gray-800"
            textAnchor="end"
            dominantBaseline="middle"
          >
            {siglaturaB}
          </text>
        </g>
      )}

      {/* Numero del cavo */}
      <g>
        <circle
          cx={xNum}
          cy={yNum}
          r={15}
          fill="#1F2937"
          opacity={0.9}
        />
        <text
          className="cable-number"
          x={xNum}
          y={yNum + 1}
          fontSize={14}
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          #{cable.originalIndex + 1}
        </text>
      </g>

      {/* Terminali */}
      <g>
        {/* ✅ Terminale A renderizzato più a sinistra */}
        {renderTerminalSchematic(cable.terminaleA, terminalAX, y1, terminalScale, 'A', 180)}
        {/* ✅ Scritte di collegamento e nome terminale usano le coordinate del terminale */}
        <text
          x={terminalAX}
          y={y1 - 20}
          className="text-sm font-bold fill-blue-800"
          textAnchor="middle"
        >
          {preprocessTextForWrapping(cable.collegamentoA)}
        </text>
        {cable.terminaleA !== 'NS' && (
          <text
            x={terminalAX}
            y={y1 + 30}
            className="text-xs font-medium fill-gray-700"
            textAnchor="middle"
          >
            {cable.terminaleA}
          </text>
        )}
      </g>

      <g>
        {/* ✅ Terminale B renderizzato più a destra */}
        {renderTerminalSchematic(cable.terminaleB, terminalBX, y2, terminalScale, 'B')}
        {/* ✅ Scritte di collegamento e nome terminale usano le coordinate del terminale */}
        <text
          x={terminalBX}
          y={y2 - 20}
          className="text-sm font-bold fill-blue-800"
          textAnchor="middle"
        >
          {preprocessTextForWrapping(cable.collegamentoB)}
        </text>
        {cable.terminaleB !== 'NS' && (
          <text
            x={terminalBX}
            y={y2 + 30}
            className="text-xs font-medium fill-gray-700"
            textAnchor="middle"
          >
            {cable.terminaleB}
          </text>
        )}
      </g>
      {/* Tooltip */}
      <g>
        <rect
          x={tooltipX - tooltipWidth / 2}
          y={75}
          width={tooltipWidth}
          height={30}
          rx={5}
          fill="white"
          stroke="#E5E7EB"
          strokeWidth={1}
          filter="drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))"
        />
        <text
          x={tooltipX}
          y={95}
          className="text-xs fill-gray-700"
          textAnchor="middle"
        >
          {tooltipText}
        </text>
      </g>
    </svg>
  );
};

export default SingleCableVisualization;